'use client'

import { useCallback, useEffect, useRef } from 'react'

interface UseDialogResetOptions {
  isOpen: boolean
  onReset: () => void
  onClose?: () => void
}

/**
 * Hook for handling dialog reset when opening.
 *
 * Uses a ref to track open state transitions, ensuring reset only runs
 * when the dialog actually opens (not on every render or dependency change).
 *
 * @param isOpen - Current open state of the dialog
 * @param onReset - Callback to reset dialog state (called when dialog opens)
 * @param onClose - Optional callback when dialog closes (for direct control cases)
 *
 * @example
 * // Context-based (e.g., with useMultiStepDialog)
 * const { isOpen } = useMultiStepDialog()
 * useDialogReset({ isOpen, onReset: handleReset })
 *
 * @example
 * // Direct control (component manages isOpen state)
 * const [isOpen, setIsOpen] = useState(false)
 * const { handleOpenChange } = useDialogReset({
 *   isOpen,
 *   onReset: handleReset,
 *   onClose: () => setIsOpen(false)
 * })
 * <Dialog open={isOpen} onOpenChange={handleOpenChange} />
 */
export function useDialogReset({ isOpen, onReset, onClose }: UseDialogResetOptions) {
  const wasOpenRef = useRef(false)

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen

    if (justOpened) {
      onReset()
    }
  }, [isOpen, onReset])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onClose?.()
    },
    [onClose]
  )

  return { handleOpenChange }
}
